import { JsonProperty } from '../../core/services/utils/mapping.service';
import { Entity } from './entity.model';
import { User } from './user.model';
import { handleGender } from '../../core/services/utils/helpers';
import { Gender } from '../../core/types/enums';
import { positionOptions } from '../utils/selectOptions';
import {
  PartnerName,
  getPartnerName,
} from '../../core/partners/partner-registry';

export class ProfileContact {
  @JsonProperty('id')
  id: number = 0;
  @JsonProperty('fname')
  fname: string = '';
  @JsonProperty('lname')
  lname: string = '';
  @JsonProperty('phone')
  phone: string = '';
  @JsonProperty('email')
  email: string = '';
  @JsonProperty('position')
  position: string = '';
  @JsonProperty('gender')
  gender: string = '';
  @JsonProperty()
  jobRole: string = '';
  @JsonProperty()
  primaryContact: boolean = false;

  constructor(init?: Partial<ProfileContact>) {
    Object.assign(this, init);
  }

  getName(): string {
    return `${handleGender(this.gender as Gender)} ${this.fname} ${this.lname}`;
  }

  getRole(): string {
    return positionOptions.find(option => option.value === this.jobRole)?.label;
  }
}

export class Address {
  @JsonProperty('id')
  id: number = 0;
  @JsonProperty()
  customerId: number = 0;
  @JsonProperty()
  nameLine1: string = '';
  @JsonProperty()
  nameLine2: string = '';
  @JsonProperty()
  streetLine1: string = '';
  @JsonProperty()
  streetLine2: string = '';
  @JsonProperty()
  houseNo: string = '';
  @JsonProperty('zip')
  zip: string = '';
  @JsonProperty('city')
  city: string = '';
  @JsonProperty('state')
  state: string = '';
  @JsonProperty('country')
  country: string = '';
  @JsonProperty()
  addressType: string = '';
  @JsonProperty('delivery')
  delivery: number = 0;
}

export class Profile extends Entity {
  @JsonProperty()
  deliveryAddress: Address = null;
  @JsonProperty()
  billingAddress: Address = null;
  @JsonProperty({ clazz: User, name: 'users' })
  users?: Array<User> = [];
  @JsonProperty('phone')
  phone: string = '';
  @JsonProperty('email')
  email: string = '';
  @JsonProperty()
  editBillingAddressInShop?: boolean = true;
  @JsonProperty('color')
  color?: string = '';
  @JsonProperty('image')
  image?: string = '';
  @JsonProperty()
  responsiblePerson: string = '';
  @JsonProperty()
  taxId?: string = '';
  @JsonProperty({ clazz: ProfileContact, name: 'contacts' })
  contacts: Array<ProfileContact> = [];
  @JsonProperty('notifications')
  notifications: Array<string> = [];
  @JsonProperty()
  partnerId?: number = 0;
  @JsonProperty()
  partnerLastSynchronization?: string = '';
  @JsonProperty()
  partnerTokenGenerated?: number = 0;

  constructor(init?: Partial<Profile>) {
    super();
    Object.assign(this, init);
  }

  /** The partner this record belongs to, or undefined if it has none. */
  get partnerName(): PartnerName | undefined {
    return getPartnerName(this.partnerId);
  }

  get isConnext(): boolean {
    return this.partnerName === PartnerName.Connext;
  }
  get isMyNeva(): boolean {
    return this.partnerName === PartnerName.Myneva;
  }
}

export class ShopCustomerDto {
  id: number;
  name: string;
  email: string;
  deliveryAddress: Address;
  billingAddress: Address;
  phone: string;
  contacts: Array<ProfileContact>;
  notifications: Array<string>;
  color: string;
  taxId?: string;

  constructor(profile: Profile) {
    this.id = profile.id;
    this.name = profile.name;
    this.email = profile.email;
    this.deliveryAddress = profile.deliveryAddress;
    this.billingAddress = profile.billingAddress;
    this.phone = profile.phone;
    this.contacts = profile.contacts;
    this.notifications = profile.notifications;
    this.color = profile.color ? profile.color : '';
    this.taxId = profile.taxId;
  }
}
